import {
  combineReducers,
  configureStore,
  createListenerMiddleware,
  Reducer,
  UnknownAction,
} from '@reduxjs/toolkit'
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux'
import { persistReducer, persistStore } from 'redux-persist'
import storage from 'redux-persist/lib/storage' // defaults to localStorage for web
import authReducer, { initialState as authInitialState } from './authSlice'
import playlistReducer, { initialState as playlistInitialState } from './playlistSlice'
import statisticsReducer, { initialState as statisticsInitialState } from './statisticsSlice'
import favsReducer, { initialState as favsInitialState } from './favsSlice'
import appReducer, { initialState as appInitialState } from './appSlice'
import searchReducer, { initialState as searchInitialState } from './searchSlice'
import ordersReducer, { initialState as ordersInitialState } from './ordersSlice'
import playlistHistoryReducer, {
  initialState as playlistHistoryInitialState,
} from './playlistHistorySlice'
import userPlaylistsReducer, {
  initialState as userPlaylistsInitialState,
} from './userPlaylistsSlice'

const persistConfig = {
  key: 'root',
  storage,
  whitelist: ['auth', 'app'],
}

const combinedReducer = combineReducers({
  auth: authReducer,
  app: appReducer,
  playlist: playlistReducer,
  statistics: statisticsReducer,
  favs: favsReducer,
  search: searchReducer,
  orders: ordersReducer,
  playlistHistory: playlistHistoryReducer,
  userPlaylists: userPlaylistsReducer,
})

const initialState = {
  auth: authInitialState,
  app: appInitialState,
  playlist: playlistInitialState,
  statistics: statisticsInitialState,
  favs: favsInitialState,
  search: searchInitialState,
  orders: ordersInitialState,
  playlistHistory: playlistHistoryInitialState,
  userPlaylists: userPlaylistsInitialState,
}

export const rootReducer: Reducer = (state: RootState, action: UnknownAction) => {
  if (action.type === 'auth/logout') {
    return combinedReducer({ ...initialState, app: state.app }, action)
  }
  return combinedReducer(state, action)
}

const persistedReducer = persistReducer(persistConfig, rootReducer)

const listenerMiddleware = createListenerMiddleware()

const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ['persist/PERSIST'],
      },
    }).prepend(listenerMiddleware.middleware),
})

const persistor = persistStore(store)
const useAppDispatch: () => AppDispatch = useDispatch
const useAppSelector: TypedUseSelectorHook<RootState> = useSelector

type RootState = ReturnType<typeof combinedReducer>
type AppDispatch = typeof store.dispatch

export {
  listenerMiddleware,
  persistor,
  store,
  useAppDispatch,
  useAppSelector,
  type AppDispatch,
  type RootState,
}
